import React, { memo, useMemo } from 'react';

import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import moment from 'moment';

import { DownloadIcon, EyeIcon, TrashIcon } from '../../assets';
import { USADateFormat, getFileType } from '../../utils';
import { Paper } from '../paper/Paper';
import { useStyles } from './AttachmentCard.styles';
import { AttachmentCardProps } from './AttachmentCard.types';
import { FilePreview } from './FilePreview';

export const AttachmentCard: React.FC<AttachmentCardProps> = memo(
  ({
    author,
    createdDate,
    customFormat,
    description,
    fileName,
    fileUrl,
    undefinedCreatedDataText = 'Recently',
    clickableFileName,
    hideInfo,
    classes,
    onView,
    onDownload,
    onDelete,
    ...props
  }) => {
    const info = useMemo(() => {
      if (hideInfo) {
        return;
      }

      let text = '';

      if (author) {
        text += `by ${author} • `;
      }

      text += createdDate
        ? typeof createdDate === 'string' && customFormat
          ? createdDate
          : moment(createdDate).format(`${customFormat || USADateFormat}`)
        : undefinedCreatedDataText;

      return text;
    }, [author, createdDate, customFormat, hideInfo, undefinedCreatedDataText]);

    const handleFileNameClick = useMemo(() => {
      if (clickableFileName && onView) {
        return () => onView();
      }

      return;
    }, [clickableFileName, onView]);

    const fileType = useMemo(() => getFileType(fileName), [fileName]);

    const shouldShowPreview = useMemo(() => {
      return !!fileUrl && (fileType === 'image' || fileType === 'pdf' || fileType === 'video');
    }, [fileUrl, fileType]);

    const styles = useStyles({
      shouldShowPreview,
      hasDescription: !!description,
    });

    return (
      <Paper elevation={24} className={classes?.paper} {...props}>
        <div className={clsx(styles.wrapper, classes?.wrapper)}>
          <div className={clsx(styles.fileIconHolder, styles.previewContainer)}>
            <FilePreview
              fileName={fileName}
              fileUrl={fileUrl}
              width={100}
              height={107}
              previewClassName={styles.preview}
              iconClassName={styles.fileIcon}
              pdfThumbnailClassName={styles.pdfThumbnail}
              pdfLoadingClassName={styles.pdfPreviewLoading}
            />
          </div>

          <div className={styles.shortInfo}>
            <Typography
              variant="subtitle1"
              className={clsx(styles.fileName, styles.primaryText, handleFileNameClick && 'clickable')}
              onClick={handleFileNameClick}
            >
              {fileName}
            </Typography>

            {!!info && (
              <Typography variant="body1" className={styles.secondaryText}>
                {info}
              </Typography>
            )}
          </div>

          <div className={styles.actions}>
            {onView && (
              <IconButton color="secondary" aria-label="view attachment" onClick={onView}>
                <EyeIcon className={styles.actionButton} />
              </IconButton>
            )}

            {onDownload && (
              <IconButton color="secondary" aria-label="view attachment" onClick={onDownload}>
                <DownloadIcon className={styles.actionButton} />
              </IconButton>
            )}

            {onDelete && (
              <IconButton color="secondary" aria-label="view attachment" onClick={onDelete}>
                <TrashIcon className={styles.actionButton} />
              </IconButton>
            )}
          </div>

          {!!description && (
            <div className={styles.description}>
              <Typography variant="body1" className={clsx(styles.secondaryText, styles.descriptionText)}>
                {description}
              </Typography>
            </div>
          )}
        </div>
      </Paper>
    );
  },
);
